import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

interface CategoryUnderlineColorSelectorProps {
  selectedTemplate: string | null;
  categoryUnderlineColor?: string;
  categoryUnderlinePrimaryColor?: string;
  categoryUnderlineSecondaryColor?: string;
  globalPrimaryColor: string;
  globalSecondaryColor: string;
  onCategoryUnderlineColorChange: (color: string) => void;
  onCategoryUnderlinePrimaryColorChange: (color: string) => void;
  onCategoryUnderlineSecondaryColorChange: (color: string) => void;
}

/**
 * CategoryUnderlineColorSelector - Color picker for category underline styles
 * Matches the exact styling of CategoryHeaderGradientSelector
 */
const CategoryUnderlineColorSelector: React.FC<CategoryUnderlineColorSelectorProps> = ({
  selectedTemplate,
  categoryUnderlineColor,
  categoryUnderlinePrimaryColor,
  categoryUnderlineSecondaryColor,
  globalPrimaryColor,
  globalSecondaryColor,
  onCategoryUnderlineColorChange,
  onCategoryUnderlinePrimaryColorChange,
  onCategoryUnderlineSecondaryColorChange
}) => {
  // Don't render if no underline template is selected
  if (!selectedTemplate || !selectedTemplate.includes('underline')) {
    return null;
  }

  const isBasicUnderline = selectedTemplate === 'category-underline';
  const isGradientUnderline = selectedTemplate === 'category-gradient-underline' || selectedTemplate === 'category-animated-underline';

  // Color presets for basic underline
  const colorPresets = [
    { name: 'Primary', color: globalPrimaryColor },
    { name: 'Secondary', color: globalSecondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Green', color: '#3cc35c' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Sand', color: '#FFFBF2' }
  ];

  // Gradient presets for gradient underlines
  const gradientPresets = [
    { name: 'Blue to Purple', primary: '#3b82f6', secondary: '#8b5cf6' },
    { name: 'Green to Blue', primary: '#10b981', secondary: '#3b82f6' },
    { name: 'Orange to Red', primary: '#f97316', secondary: '#ef4444' },
    { name: 'Pink to Rose', primary: '#ec4899', secondary: '#f43f5e' },
    { name: 'Gold to Orange', primary: '#f59e0b', secondary: '#f97316' },
    { name: 'Teal to Green', primary: '#14b8a6', secondary: '#10b981' }
  ];

  const handleColorPresetClick = (color: string) => {
    onCategoryUnderlineColorChange(color);
  };

  const handleGradientPresetClick = (preset: typeof gradientPresets[0]) => {
    onCategoryUnderlinePrimaryColorChange(preset.primary);
    onCategoryUnderlineSecondaryColorChange(preset.secondary);
  };

  const handleSwapColors = () => {
    const currentPrimary = categoryUnderlinePrimaryColor || globalPrimaryColor;
    const currentSecondary = categoryUnderlineSecondaryColor || globalSecondaryColor;
    onCategoryUnderlinePrimaryColorChange(currentSecondary);
    onCategoryUnderlineSecondaryColorChange(currentPrimary);
  };

  const handleResetToGlobal = () => {
    if (isBasicUnderline) {
      onCategoryUnderlineColorChange(globalPrimaryColor);
    } else if (isGradientUnderline) {
      onCategoryUnderlinePrimaryColorChange(globalPrimaryColor);
      onCategoryUnderlineSecondaryColorChange(globalSecondaryColor);
    }
  };

  const currentUnderlineColor = categoryUnderlineColor || globalPrimaryColor;
  const currentPrimaryColor = categoryUnderlinePrimaryColor || globalPrimaryColor;
  const currentSecondaryColor = categoryUnderlineSecondaryColor || globalSecondaryColor;

  const isUsingGlobalColors = isBasicUnderline 
    ? currentUnderlineColor === globalPrimaryColor
    : currentPrimaryColor === globalPrimaryColor && currentSecondaryColor === globalSecondaryColor;

  if (isBasicUnderline) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Underline Colour</Label>
          <div 
            className="w-20 h-6 rounded border border-gray-300"
            style={{ backgroundColor: currentUnderlineColor }}
          />
        </div>
        
        <div className="space-y-4">
          {/* Single Color Control */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Colour</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: currentUnderlineColor }}
                  />
                  {currentUnderlineColor}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Pick underline colour</Label>
                  <Input
                    type="color"
                    value={currentUnderlineColor}
                    onChange={(e) => onCategoryUnderlineColorChange(e.target.value)}
                    className="w-full h-10"
                  />
                  <Input
                    type="text"
                    value={currentUnderlineColor}
                    onChange={(e) => onCategoryUnderlineColorChange(e.target.value)}
                    placeholder="#ffffff"
                    className="font-mono text-sm"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reset Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToGlobal}
            disabled={isUsingGlobalColors}
            className="w-full"
          >
            ↺ Reset to Global
          </Button>

          {/* Color Presets */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Quick Presets</Label>
            <div className="grid grid-cols-2 gap-2">
              {colorPresets.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => handleColorPresetClick(preset.color)}
                  className="justify-start gap-2 h-auto p-2"
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: preset.color }}
                  />
                  <span className="text-xs text-left">{preset.name}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500">
          Configure the colour for the category underline effect
        </div>
      </div>
    );
  }

  if (isGradientUnderline) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Gradient Colours</Label>
          <div 
            className="w-20 h-6 rounded border border-gray-300"
            style={{ 
              background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)` 
            }}
          />
        </div>
        
        <div className="space-y-4">
          {/* Color Controls */}
          <div className="grid grid-cols-2 gap-3">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-600">Primary Colour</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                  >
                    <div 
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: currentPrimaryColor }}
                    />
                    {currentPrimaryColor}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Pick primary colour</Label>
                    <Input
                      type="color"
                      value={currentPrimaryColor}
                      onChange={(e) => onCategoryUnderlinePrimaryColorChange(e.target.value)}
                      className="w-full h-10"
                    />
                    <Input
                      type="text"
                      value={currentPrimaryColor}
                      onChange={(e) => onCategoryUnderlinePrimaryColorChange(e.target.value)}
                      placeholder="#ffffff"
                      className="font-mono text-sm"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-600">Secondary Colour</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                  >
                    <div 
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: currentSecondaryColor }}
                    />
                    {currentSecondaryColor}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Pick secondary colour</Label>
                    <Input
                      type="color"
                      value={currentSecondaryColor}
                      onChange={(e) => onCategoryUnderlineSecondaryColorChange(e.target.value)}
                      className="w-full h-10"
                    />
                    <Input
                      type="text"
                      value={currentSecondaryColor}
                      onChange={(e) => onCategoryUnderlineSecondaryColorChange(e.target.value)}
                      placeholder="#ffffff"
                      className="font-mono text-sm"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSwapColors}
              className="w-full"
            >
              ↔ Swap Colours
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToGlobal}
              disabled={isUsingGlobalColors}
              className="w-full"
            >
              ↺ Reset to Global
            </Button>
          </div>

          {/* Gradient Presets */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Quick Presets</Label>
            <div className="grid grid-cols-2 gap-2">
              {gradientPresets.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => handleGradientPresetClick(preset)}
                  className="justify-start gap-2 h-auto p-2"
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                    style={{ 
                      background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)` 
                    }}
                  />
                  <span className="text-xs text-left">{preset.name}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500">
          Configure the primary and secondary colours for the gradient underline effect
        </div>
      </div>
    );
  }

  return null;
};

export default CategoryUnderlineColorSelector;